export const isMac = () => navigator.platform.toUpperCase().indexOf("MAC") >= 0;

// Route patterns used by the cheatsheet to filter shortcuts to the current page.
// A shortcut without `routes` is always shown.
const ADMISSION_DETAIL = /^\/admissions\/[^/]+$/;
const ADMISSION_LIST = /^\/admissions$/;
const PRESCRIPTION_DETAIL = /^\/prescription\/[^/]+$/;
const LIST_ROUTES = /^\/(admissions|prescription|code|laboratory|guideline|question|action|table|clinic)$/;
const EDIT_ROUTES = /^\/(code|laboratory|guideline|question|action|clinic|table)\/[^/]+\/edit$/;
const ANNOTATION_EDITOR = /^\/annotation\/[^/]+(\/[^/]+)?$/;

// Single source of truth for the named, discoverable keyboard shortcuts (cheatsheet +
// pro-tips + inline badges). `keys` uses react-hotkeys-hook syntax: "mod+s" (mod = ⌘ on
// Mac, Ctrl elsewhere), "mod+shift+s", sequences with ">" ("g>a"). `group` drives the
// cheatsheet sections. Contextual vim-style bindings (j/k/[/]) live with their component.
export const SHORTCUTS = {
  TOGGLE_PALETTE: { keys: "mod+k", labelFr: "Recherche rapide", group: "Global" },
  OPEN_CHEATSHEET: { keys: "?, f1", labelFr: "Afficher les raccourcis", group: "Global" },
  BACK: { keys: "escape", labelFr: "Fermer / Retour", group: "Global" },
  TOGGLE_SIDEBAR: { keys: "mod+b", labelFr: "Afficher / cacher le panneau", group: "Global", role: "admin" },

  GO_ADMISSIONS: { keys: "g>a", labelFr: "Aller aux admissions", group: "Navigation" },
  GO_ORDONNANCES: { keys: "g>o", labelFr: "Aller aux ordonnances", group: "Navigation" },
  GO_TABLE: { keys: "g>t", labelFr: "Aller à la table", group: "Navigation", role: "admin" },
  GO_CODES: { keys: "g>c", labelFr: "Aller aux codes", group: "Navigation", role: "admin" },
  GO_STATS: { keys: "g>s", labelFr: "Aller aux statistiques", group: "Navigation", role: "admin" },
  GO_QUESTIONS: { keys: "g>q", labelFr: "Aller aux questions", group: "Navigation", role: "admin" },

  NAV_PREV: { keys: "up, k", labelFr: "Élément précédent", group: "Listes", routes: [LIST_ROUTES, EDIT_ROUTES, ADMISSION_DETAIL, PRESCRIPTION_DETAIL] },
  NAV_NEXT: { keys: "down, j", labelFr: "Élément suivant", group: "Listes", routes: [LIST_ROUTES, EDIT_ROUTES, ADMISSION_DETAIL, PRESCRIPTION_DETAIL] },
  NAV_OPEN: { keys: "enter", labelFr: "Ouvrir l'élément", group: "Listes", routes: [LIST_ROUTES] },
  OPEN_NEW_TAB: { keys: "mod+enter", labelFr: "Ouvrir dans un nouvel onglet", group: "Listes", routes: [LIST_ROUTES] },
  FOCUS_SEARCH: { keys: "/", labelFr: "Rechercher", group: "Listes", routes: [LIST_ROUTES] },
  FOCUS_FILTER: { keys: "f", labelFr: "Filtrer", group: "Listes", routes: [LIST_ROUTES] },
  CREATE: { keys: "c", labelFr: "Nouvel élément", group: "Listes", routes: [LIST_ROUTES] },

  SAVE: { keys: "mod+s", labelFr: "Valider le dossier", group: "Admission", routes: [ADMISSION_DETAIL] },
  EDIT_SAVE: { keys: "mod+s", labelFr: "Enregistrer", group: "Édition", routes: [EDIT_ROUTES] },
  SAVE_AND_SEND: { keys: "mod+shift+s", labelFr: "Envoyer en production", group: "Admission", routes: [ADMISSION_DETAIL] },
  REJECT: { keys: "mod+d", labelFr: "Rejeter le dossier", group: "Admission", routes: [ADMISSION_DETAIL] },
  SELECT_ALL: { keys: "mod+a", labelFr: "Tout sélectionner", group: "Admission", routes: [ADMISSION_LIST] },
  TOGGLE_SELECT: { keys: "x", labelFr: "Sélectionner / désélectionner", group: "Admission", routes: [ADMISSION_LIST] },
  UNSELECT_ALL: { keys: "u", labelFr: "Tout désélectionner", group: "Admission", routes: [ADMISSION_LIST] },

  DOC_PREV: { keys: "[", labelFr: "Document précédent", group: "Documents", routes: [ADMISSION_DETAIL] },
  DOC_NEXT: { keys: "]", labelFr: "Document suivant", group: "Documents", routes: [ADMISSION_DETAIL] },

  TAB_PREV: { keys: "left, w", labelFr: "Section précédente", group: "Sections", routes: [ADMISSION_DETAIL] },
  TAB_NEXT: { keys: "right, b", labelFr: "Section suivante", group: "Sections", routes: [ADMISSION_DETAIL] },

  ANNOTATION_SAVE: { keys: "mod+s", labelFr: "Enregistrer le document", group: "Annotation", routes: [ANNOTATION_EDITOR] },
  ANNOTATION_VALIDATE: { keys: "v", labelFr: "Valider et passer au suivant", group: "Annotation", routes: [ANNOTATION_EDITOR] },
  ANNOTATION_NEXT: { keys: "j, right", labelFr: "Document suivant", group: "Annotation", routes: [ANNOTATION_EDITOR] },
  ANNOTATION_PREV: { keys: "k, left", labelFr: "Document précédent", group: "Annotation", routes: [ANNOTATION_EDITOR] },
  ANNOTATION_LABEL: { keys: "l", labelFr: "Choisir le label", group: "Annotation", routes: [ANNOTATION_EDITOR] },
  ANNOTATION_MANUAL: { keys: "m", labelFr: "Annotation manuelle (zone)", group: "Annotation", routes: [ANNOTATION_EDITOR] },
  ANNOTATION_ACCEPT: { keys: "enter", labelFr: "Accepter la recommandation", group: "Annotation", routes: [ANNOTATION_EDITOR] },
  ANNOTATION_REJECT: { keys: "n", labelFr: "Rejeter la recommandation", group: "Annotation", routes: [ANNOTATION_EDITOR] },
  ANNOTATION_DELETE: { keys: "delete, backspace", labelFr: "Supprimer le document", group: "Annotation", routes: [ANNOTATION_EDITOR] },
  ANNOTATION_UNDO: { keys: "z", labelFr: "Annuler la suppression", group: "Annotation", routes: [ANNOTATION_EDITOR] },
};

export const isShortcutActiveOn = (shortcut, pathname) => !shortcut.routes || shortcut.routes.some((r) => r.test(pathname));

const MAC_SYMBOLS = { mod: "⌘", meta: "⌘", cmd: "⌘", ctrl: "⌃", alt: "⌥", shift: "⇧" };
const PC_SYMBOLS = { mod: "Ctrl", meta: "Ctrl", cmd: "Ctrl", ctrl: "Ctrl", alt: "Alt", shift: "Shift" };
const KEY_SYMBOLS = {
  up: "↑",
  down: "↓",
  left: "←",
  right: "→",
  arrowup: "↑",
  arrowdown: "↓",
  arrowleft: "←",
  arrowright: "→",
  enter: "↵",
  escape: "Échap",
  delete: "⌦",
  backspace: "⌫",
  " ": "Espace",
};

const formatToken = (token) => {
  const lower = token.toLowerCase();
  const modifiers = isMac() ? MAC_SYMBOLS : PC_SYMBOLS;
  if (modifiers[lower]) return modifiers[lower];
  if (KEY_SYMBOLS[lower]) return KEY_SYMBOLS[lower];
  if (lower === "f1") return "F1";
  return token.toUpperCase();
};

// Turns a react-hotkeys-hook key string into nested display rows: [binding][step][token].
// "mod+s"     → [[["⌘", "S"]]]                (one binding)
// "g>a"       → [[["G"], ["A"]]]              (one binding, sequence of two steps)
// "up, k"     → [[["↑"]], [["K"]]]            (two alternative bindings)
// "?, f1"     → [[["?"]], [["F1"]]]
export const formatKeys = (keys) =>
  keys.split(",").map((binding) =>
    binding
      .trim()
      .split(">")
      .map((step) => step.split("+").map(formatToken)),
  );
